import { useEffect, useRef, useState } from 'react';
import { usePortalSetting } from '@/hooks/usePortalSetting';

function StatItem({ value, suffix, unit, label, icon }: { value: number; suffix: string; unit: string; label: string; icon: string }) {
  const [display, setDisplay] = useState(0);
  const startedRef = useRef(false);
  const itemRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = itemRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const duration = 1400;
            const start = performance.now();
            const tick = (now: number) => {
              const progress = Math.min((now - start) / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3);
              setDisplay(Math.round(value * eased));
              if (progress < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [value]);

  return (
    <div ref={itemRef} className="flex flex-col items-center text-center gap-2 px-2">
      <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50/10 text-secondary-300 mb-1">
        <i className={`${icon} text-2xl`}></i>
      </span>
      <div className="flex items-baseline gap-1">
        <span className="font-heading font-bold text-3xl md:text-4xl text-background-50">{display.toLocaleString()}</span>
        {suffix && <span className="font-heading font-bold text-2xl md:text-3xl text-secondary-300">{suffix}</span>}
        {unit && <span className="text-base text-background-100/80 ml-1">{unit}</span>}
      </div>
      <span className="text-sm text-background-100/75">{label}</span>
    </div>
  );
}

export default function StatsOverview() {
  // 官网配置接口，失败时降级为 mock 数据
  const { stats } = usePortalSetting();

  return (
    <section id="stats" className="scroll-mt-24 w-full px-4 md:px-8 py-14 md:py-20">
      <div className="max-w-7xl mx-auto">
        <div className="relative overflow-hidden rounded-lg bg-gradient-to-br from-primary-800 via-primary-900 to-primary-950 px-6 md:px-10 py-10 md:py-14">
          <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-secondary-500/10"></div>
          <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-accent-500/10"></div>
          <div className="relative">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 text-balance">平台数据概览</h2>
              <p className="mt-3 text-sm text-background-100/75">持续汇聚中西医结合的优质资源与服务能力</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6">
              {stats.map((item) => (
                <StatItem key={item.id} value={item.value} suffix={item.suffix} unit={item.unit} label={item.label} icon={item.icon} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}